import { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, type FieldPath } from "react-hook-form";

import { useLocalization } from "@i18n";

import type {
  AdminStructureDetail,
  PatchAdminStructureHydrologyRequest,
  StructureType,
} from "../../api/structures/structures.types";
import { usePatchAdminStructureHydrologyMutation } from "../../api/structures/structures.mutations";
import {
  createAdminStructureHydrologySchema,
  mapDetailToHydrologyForm,
  type AdminStructureHydrologyForm,
} from "../../schemas";
import { STRUCTURE_HYDROLOGY_FORM_ID } from "./structure-modal-form-ids";

type HydrologyFieldConfig = {
  name: FieldPath<AdminStructureHydrologyForm>;
  labelKey: string;
  kind: "text" | "select";
};

const DAM_HYDROLOGY_FIELDS: HydrologyFieldConfig[] = [
  {
    name: "maximumMaximorumLevel",
    labelKey: "admin.structures.hydrology.label.maximumMaximorumLevel",
    kind: "text",
  },
  {
    name: "criticalDuration",
    labelKey: "admin.structures.hydrology.label.criticalDuration",
    kind: "text",
  },
  {
    name: "inflowRate",
    labelKey: "admin.structures.hydrology.label.inflowRate",
    kind: "text",
  },
  {
    name: "outflowRate",
    labelKey: "admin.structures.hydrology.label.outflowRate",
    kind: "text",
  },
  {
    name: "designRainfall",
    labelKey: "admin.structures.hydrology.label.designRainfall",
    kind: "text",
  },
  {
    name: "contributingBasinArea",
    labelKey: "admin.structures.hydrology.label.contributingBasinArea",
    kind: "text",
  },
  {
    name: "returnPeriod",
    labelKey: "admin.structures.hydrology.label.returnPeriod",
    kind: "text",
  },
  {
    name: "totalVolumeAtCrestElevation",
    labelKey: "admin.structures.hydrology.label.totalVolumeAtCrestElevation",
    kind: "text",
  },
  {
    name: "spillwayType",
    labelKey: "admin.structures.hydrology.label.spillwayType",
    kind: "select",
  },
  {
    name: "spillwaySill",
    labelKey: "admin.structures.hydrology.label.spillwaySill",
    kind: "text",
  },
  {
    name: "referenceDocument",
    labelKey: "admin.structures.hydrology.label.referenceDocument",
    kind: "text",
  },
];

const STOCKPILE_HYDROLOGY_FIELDS: HydrologyFieldConfig[] = [
  {
    name: "criticalDuration",
    labelKey: "admin.structures.hydrology.label.criticalDuration",
    kind: "text",
  },
  {
    name: "outflowRate",
    labelKey: "admin.structures.hydrology.label.outflowRate",
    kind: "text",
  },
  {
    name: "designRainfall",
    labelKey: "admin.structures.hydrology.label.designRainfall",
    kind: "text",
  },
  {
    name: "returnPeriod",
    labelKey: "admin.structures.hydrology.label.returnPeriod",
    kind: "text",
  },
  {
    name: "referenceDocument",
    labelKey: "admin.structures.hydrology.label.referenceDocument",
    kind: "text",
  },
];

const SPILLWAY_OPTIONS = [
  "surface",
  "tulip",
  "lateralChannel",
  "labyrinth",
  "gated",
  "bottom",
] as const;

export type AdminStructureHydrologyTabProps = {
  complexId: string;
  structureId: string;
  structureType: StructureType;
  canWrite: boolean;
  detail: AdminStructureDetail;
  onSaved: (detail: AdminStructureDetail) => void;
  onSavingChange?: (isSaving: boolean) => void;
};

const AdminStructureHydrologyTab = ({
  complexId,
  structureId,
  structureType,
  canWrite,
  detail,
  onSaved,
  onSavingChange,
}: AdminStructureHydrologyTabProps) => {
  const { translate } = useLocalization();
  const patchMutation = usePatchAdminStructureHydrologyMutation();
  const schema = useMemo(
    () =>
      structureType === "openPit"
        ? null
        : createAdminStructureHydrologySchema(translate, structureType),
    [structureType, translate],
  );

  const form = useForm<AdminStructureHydrologyForm>({
    resolver: schema ? zodResolver(schema) : undefined,
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: mapDetailToHydrologyForm(detail, structureType) ?? undefined,
  });

  useEffect(() => {
    if (structureType === "openPit") {
      return;
    }
    const values = mapDetailToHydrologyForm(detail, structureType);
    if (values) {
      form.reset(values);
    }
  }, [detail, form, structureType]);

  useEffect(() => {
    onSavingChange?.(patchMutation.isPending);
  }, [onSavingChange, patchMutation.isPending]);

  if (structureType === "openPit") {
    return null;
  }

  const onSubmit = async (data: AdminStructureHydrologyForm) => {
    const saved = await patchMutation.mutateAsync({
      complexId,
      structureId,
      payload: data as PatchAdminStructureHydrologyRequest,
    });
    onSaved(saved);
    const values = mapDetailToHydrologyForm(saved, structureType);
    if (values) {
      form.reset(values);
    }
  };

  const fields =
    structureType === "stockpile" ? STOCKPILE_HYDROLOGY_FIELDS : DAM_HYDROLOGY_FIELDS;

  return (
    <form
      id={STRUCTURE_HYDROLOGY_FORM_ID}
      noValidate
      onSubmit={form.handleSubmit(onSubmit, () => {
        void form.trigger(undefined, { shouldFocus: true });
      })}
      className="flex flex-col gap-4"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {fields.map(({ name, labelKey, kind }) => {
          const label = translate(labelKey);
          return (
            <Controller
              key={name}
              control={form.control}
              name={name}
              render={({ field, fieldState }) => (
                <label className="group flex min-w-0 w-full flex-col gap-1">
                  <span className="text-label group-aria-invalid:text-error">
                    {label}
                  </span>
                  {kind === "select" ? (
                    <select
                      aria-label={label}
                      aria-invalid={Boolean(fieldState.error) || undefined}
                      disabled={!canWrite}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                      className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm shadow-sm disabled:opacity-60 aria-invalid:border-error aria-invalid:ring-[3px] aria-invalid:ring-error/20"
                    >
                      {SPILLWAY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {translate(`admin.structures.hydrology.spillwayType.${option}`)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      aria-label={label}
                      aria-invalid={Boolean(fieldState.error) || undefined}
                      disabled={!canWrite}
                      {...field}
                      className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm shadow-sm disabled:opacity-60 aria-invalid:border-error aria-invalid:ring-[3px] aria-invalid:ring-error/20"
                    />
                  )}
                  {fieldState.error?.message ? (
                    <span className="text-error text-sm">
                      {fieldState.error.message}
                    </span>
                  ) : null}
                </label>
              )}
            />
          );
        })}
      </div>
    </form>
  );
};

export default AdminStructureHydrologyTab;
